import React, { useCallback, useEffect, useRef, useContext } from 'react';
import { message } from 'antd';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { Content } from '../styles';
import PageElementsProvider, { PageElementsContext } from '../../../context/pageElementsContext';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import BlockElementRenderer from '../CreativeBlockDesign/BlockElementRenderer';
import GridContentResizer from './GridContentResizer';
import { processIfSVG } from '../../../utils/svgUtils';
import { STATIC_TABLE_BLOCK, TEXT_ELEMENT, ELEMENTS, IMAGE_ELEMENT, SVG_ELEMENT, BARCODE_ELEMENT,
 STATIC_TABLE_ELEMENT, ELEMENT_TYPES, FILLABLE_TEXT_INPUT_TYPE, FILLABLE_ELEMENT, FILLABLE_COMBO_BOX_TYPE, FILLABLE_SIGNATURE_TYPE, FILLABLE_CHECKBOX_TYPE, FILLABLE_RADIO_BUTTON_TYPE, 
 FILLABLE_CHECKBOX_GROUP_TYPE,
 FILLABLE_RADIO_BUTTON_GROUP_TYPE,
 FILLABLE_COMBOBOX_GROUP_TYPE , BARCODE_39} from '../../../utils/complexDocumentConstants';
import { random4Characters } from '../../../utils/javascript';
import { DYNAMIC_TABLE_OPERATIONS } from '../DynamicTableDesign/translationValues';

const UNSUPPORTED_ELEMENTS = [STATIC_TABLE_ELEMENT];

const HANDLERS_FOR_ELEMENTS = {
  ['rectangle']: ['s', 'e', 'se'],
  ['circle']: ['se'],
  ['line']: ['e', 'w'],
  ['doubleLine'] : ['e', 'w'],
  [IMAGE_ELEMENT]: ['se'],
  [SVG_ELEMENT]: ['se'],
  [BARCODE_ELEMENT]: ['se'],
  [FILLABLE_TEXT_INPUT_TYPE]: ['se', 's', 'n', 'e', 'w'],
  [FILLABLE_SIGNATURE_TYPE]: ['se', 's', 'n', 'e', 'w'],
  [FILLABLE_COMBO_BOX_TYPE]: ['se', 's', 'n', 'e', 'w'],
  [FILLABLE_CHECKBOX_TYPE]: ['se'],
  [FILLABLE_RADIO_BUTTON_TYPE]: ['se'],
  [ELEMENT_TYPES.CHART]: ['se']
}

const DEFAULT_TABLE_CELL = (uuid, parentBlockUUID) => ({
  uuid,
  style: {},
  contentType: TEXT_ELEMENT,
  isChild: true,
  parentBlockUUID: parentBlockUUID,
  contents: [
    {
      contentType: 'PARAGRAPH',
      style: {
        lineSpacing: 1.5,
        fontWeight: [],
        color: '#000000',
      },
      content: '',
    },
    {
      contentType: 'LINE_BREAK',
      content: '\n',
      style: {
        lineSpacing: 1.5,
        alignment: 'center',
        color: '#000000',
      },
    },
  ],
  variableContract: []
});

const CellContainer = (props, moveableRef) => {
  const { elementUUID, parentBlockUUID, selected, editable, scaling, pageId, adjustScroll, parentUUID,
    verticalAlignment = 'center', horizontalAlignment = 'center', onCellDoubleClick, cellPadding, setIsOverCellUUID, t } = props;

  const cellRef = useRef(null);

  const customSelector = useCallback(store => store[elementUUID], []);
  const [element, methods] = useDocumentContext(customSelector, `CellContainer-${elementUUID}`);

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(customVarSelector);

  const { updateComplexElementDetails, storedElements } = useContext(PageElementsContext);

  const onCellDrop = useCallback(async (item, direction = 'over', monitor = undefined) => {
    let newElement = Object.assign({}, item.element);
    if (monitor?.getItemType() === '__NATIVE_FILE__') {
      // TODO : Handle native files
      /*
        const uploadedImages = await handleExternalFileDrop(element, monitor);
        uploadedImages.forEach(item => {
          const { response, dimensions } = item;
          const { id, imageLink } = response;
          const details = {
            type: 'elements',
            element: {
              type: 'colored',
              elementDetails: {
                styles: {
                  position: 'absolute',
                  backgroundColor: 'transparent',
                  height: `${dimensions.height}px`,
                  width: `${dimensions.width}px`,
                },
                type: 'image',
                value: imageLink,
              },
              id,
              name: `upload-${id}`,
              thumbnail: imageLink,
            },
            boundaries: {},
          };
          handleElementDrop(details, monitor);
        });
      }
      */
    } else {
      if (UNSUPPORTED_ELEMENTS.includes(newElement.elementDetails.type)) {
        message.warn(t(DYNAMIC_TABLE_OPERATIONS.DROPPED_ELEMENT_NOT_SUPPORT_WARN.key, DYNAMIC_TABLE_OPERATIONS.DROPPED_ELEMENT_NOT_SUPPORT_WARN.value));
        return;
      }
    }
    newElement.contentType = newElement.elementDetails.type;
    if (newElement.elementDetails.type === IMAGE_ELEMENT) {
      const { value, styles, uiType } = newElement['elementDetails'];
      if (UNSUPPORTED_ELEMENTS.includes(uiType)) {
        message.warn(t(DYNAMIC_TABLE_OPERATIONS.DROPPED_ELEMENT_NOT_SUPPORT_WARN.key, DYNAMIC_TABLE_OPERATIONS.DROPPED_ELEMENT_NOT_SUPPORT_WARN.value));
        return;
      }
      const isSvgLine = ['line', 'doubleLine'].includes(uiType);
      const svgStyles = isSvgLine ? {width: uiType==='doubleLine'? '200%' : '100%', height: uiType==='doubleLine'? '15px' :'5px'} : {width: '100%', height: '100%'}
      const isPixels =  isSvgLine ? true : false;
      const newValue = await processIfSVG(value, svgStyles, uiType, true, isPixels, cellRef.current);
      newElement.url = newValue;
      newElement.contentType = value !== newValue ? SVG_ELEMENT : IMAGE_ELEMENT;
      if (newElement.contentType === SVG_ELEMENT) {
        newElement.style = {};
        newElement.uiType = newElement.elementDetails.uiType;
      }
      newElement.style = {
        width: 100,
        ...(isSvgLine && { height: uiType === 'doubleLine' ? 15 : 5 })
      }
    } else if (newElement.elementDetails.type === BARCODE_ELEMENT) {
      newElement.barcodeType = newElement.elementDetails.barcodeType;
      newElement.style = {
        isVariable: false
      };
      newElement.style = {
        width: 100,
        height: 100
      }
      newElement.content = `Sample ${newElement.elementDetails.barcodeType} barcode`;
      if (newElement.elementDetails.barcodeType === BARCODE_39) {
        newElement.content = `SAMPLE ${item.element.elementDetails.barcodeType.toUpperCase()} BARCODE`;
      }
    } else if (newElement.elementDetails.type === ELEMENT_TYPES.CHART) {
      newElement.style = {
        border: newElement.elementDetails.styles.border,
        backgroundColor: newElement.elementDetails.styles.backgroundColor,
        height: 100,
        width: 100,
      };
      newElement.contents = newElement.elementDetails.chartDetails;
      newElement.contents.baseVariable = `chartvar-${random4Characters()}`;
    } else if (newElement.elementDetails.type === 'textbox')  {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_TEXT_INPUT_TYPE;
      newElement.contents = item.element.elementDetails.contents;
      newElement.formOptions = item.element.elementDetails.formOptions;
      newElement.style = Object.assign({}, {
        width: 100,
        height: 26,
        rotate: item.element.elementDetails.styles.rotate
      }, item.element.elementDetails?.additionalStyles || {});
    } else if (newElement.elementDetails.type === FILLABLE_COMBO_BOX_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_COMBO_BOX_TYPE;
      newElement.contents = item.element.elementDetails.contents;
      newElement.formOptions = item.element.elementDetails.formOptions;
      newElement.style = Object.assign({}, {
        width: 100,
        height: 26,
        rotate: item.element.elementDetails.styles.rotate
      }, item.element.elementDetails?.additionalStyles || {});
    } else if (item.element.elementDetails.type === FILLABLE_SIGNATURE_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_SIGNATURE_TYPE;
      newElement.content = item.element.elementDetails.content;
      newElement.formOptions = item.element.elementDetails.formOptions;
      newElement.style = Object.assign({}, {
        width: 100,
        height: 26,
        rotate: item.element.elementDetails.styles.rotate
      }, item.element.elementDetails?.additionalStyles || {});
    } else if (item.element.elementDetails.type === FILLABLE_CHECKBOX_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_CHECKBOX_TYPE;
      newElement.formOptions = item.element.elementDetails.formOptions;
      newElement.style = Object.assign({}, item.element.elementDetails?.additionalStyles || {}, {
        width: 22,
        height: 22,
        rotate: item.element.elementDetails.styles.rotate,
      });
     } else if (item.element.elementDetails.type === FILLABLE_RADIO_BUTTON_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_RADIO_BUTTON_TYPE;
      newElement.formOptions = item.element.elementDetails.formOptions;
      newElement.style = Object.assign({}, item.element.elementDetails?.additionalStyles || {}, {
        width: 22,
        height: 22,
        rotate: item.element.elementDetails.styles.rotate,
      });
     } else if (item.element.elementDetails.type === FILLABLE_CHECKBOX_GROUP_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_CHECKBOX_GROUP_TYPE;
      // newElement.contents = item.element.elementDetails.contents;
      newElement.style = Object.assign(
        {},
        item.element.elementDetails?.style,
      );
     }  else if (item.element.elementDetails.type === FILLABLE_RADIO_BUTTON_GROUP_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_RADIO_BUTTON_GROUP_TYPE;
      newElement.style = Object.assign(
        {},
        item.element.elementDetails?.style,
      );
     } else if (item.element.elementDetails.type === FILLABLE_COMBOBOX_GROUP_TYPE) {
      newElement.contentType = FILLABLE_ELEMENT;
      newElement.fillableType = FILLABLE_COMBOBOX_GROUP_TYPE;
      newElement.formOptions = item.element.elementDetails.formOptions;
      newElement.style = Object.assign({}, item.element.elementDetails?.style, {
        width: 100,
        height: 26,
      });
     } else {
      newElement.contents = [...(newElement.elementDetails.contents || [])];
      newElement.style = { width: 100 };
    }
    console.log({ newElement, direction, elementUUID });
    const droppedElement = {
      uuid: elementUUID,
      style: newElement.style,
      contentType: newElement.contentType,
      fillableType: newElement.fillableType,
      formOptions: newElement.formOptions,
      contents: newElement.elementDetails.chartDetails ? newElement.elementDetails.chartDetails : item.element.elementDetails.type === FILLABLE_CHECKBOX_GROUP_TYPE || item.element.elementDetails.type === FILLABLE_RADIO_BUTTON_GROUP_TYPE || item.element.elementDetails.type === FILLABLE_COMBOBOX_GROUP_TYPE ? newElement.elementDetails.contents : [...(newElement.elementDetails.contents || [])],
      url: newElement.url,
      uiType: newElement.uiType,
      isChild: true,
      parentBlockUUID: parentBlockUUID,
      ...(newElement.contentType === BARCODE_ELEMENT ? {
        content: newElement.content,
        barcodeType: newElement.barcodeType
      } : {}),
      variableContract: []
    }

    const currentParentElement = methods.get()[parentBlockUUID];
    const currentElement = currentParentElement.contents.find(cell => cell.uuid === elementUUID);
    if (direction === 'over') {
      // TODO : Auto save and undo redo
      /*methods.set({
        [elementUUID]: droppedElement
      });*/
      methods.updateBlock(
        'static-table-cells',
        parentBlockUUID,
        {
          droppedElement: droppedElement,
          flowUUID: parentUUID,
          type: 'drop',
          elementUUID: elementUUID
        }
      )
    } else if (direction === 'left') {
      updateComplexElementDetails(
        parentBlockUUID,
        {
          index: currentElement.column - 1,
          isLeft: true,
          newElement: droppedElement,
          rowIndex: currentElement.row
        },
        'static-block-add-column'
      );
    } else if (direction === 'right') {
      updateComplexElementDetails(
        parentBlockUUID,
        {
          index: currentElement.column - 1,
          isLeft: false,
          newElement: droppedElement,
          rowIndex: currentElement.row
        },
        'static-block-add-column'
      );
    } else if (direction === 'top') {
      updateComplexElementDetails(
        parentBlockUUID,
        {
          index: currentElement.row - 1,
          isTop: true,
          isBlock: true,
          newElement: droppedElement,
          colIndex: currentElement.column
        },
        'static-table-add-row'
      );
    } else if (direction === 'bottom') {
      updateComplexElementDetails(
        parentBlockUUID,
        {
          index: currentElement.row - 1,
          isTop: false,
          isBlock: true,
          newElement: droppedElement,
          colIndex: currentElement.column
        },
        'static-table-add-row'
      );
    }
    return true;
  }, []);

  const [isOver, drop] = useDrop({
    canDrop: (item) => editable && !selected,
    accept: [ELEMENTS, NativeTypes.FILE],
    drop: (item, monitor) => {
      /*if (item.element?.elementDetails?.uuid === uuid) {
        return { drop: true };
      }*/
      if (!monitor.didDrop()) {
        return onCellDrop(item, 'over', monitor);
      }
    },
    collect: monitor => !!monitor.isOver()/* && monitor.getItem().element?.elementDetails?.uuid !== uuid*/,
  });

  useEffect(() => {
    isOver ? setIsOverCellUUID(elementUUID) : setIsOverCellUUID('');
  }, [isOver]);

  const [isOverLeft, leftDrop] = useDrop({
    canDrop: (item) => editable && !selected,
    accept: [ELEMENTS, NativeTypes.FILE],
    drop: (item, monitor) => {
      return onCellDrop(item, 'left', monitor);
    },
    collect: monitor => !!monitor.isOver(),
  });

  const [isOverRight, rightDrop] = useDrop({
    canDrop: (item) => editable && !selected,
    accept: [ELEMENTS, NativeTypes.FILE],
    drop: (item, monitor) => {
      return onCellDrop(item, 'right', monitor);
    },
    collect: monitor => !!monitor.isOver(),
  });

  const [isOverTop, topDrop] = useDrop({
    canDrop: (item) => editable && !selected,
    accept: [ELEMENTS, NativeTypes.FILE],
    drop: (item, monitor) => {
      return onCellDrop(item, 'top', monitor);
    },
    collect: monitor => !!monitor.isOver(),
  });

  const [isOverBottom, bottomDrop] = useDrop({
    canDrop: (item) => editable && !selected,
    accept: [ELEMENTS, NativeTypes.FILE],
    drop: (item, monitor) => {
      return onCellDrop(item, 'bottom', monitor);
    },
    collect: monitor => !!monitor.isOver(),
  });

  const updateCellContents = useCallback((cellContents) => {
    // TODO: Auto save the parent element (static table as part of changes in cell, and coordinate with undo redo)
    methods.updateBlock('contents', elementUUID, { contents: cellContents, flowUUID: parentUUID, type: STATIC_TABLE_BLOCK }, true, globalVariables);
  }, [parentUUID, globalVariables]);

  useEffect(() => {
    if (selected && element === undefined) {
      let defaultCellContent = DEFAULT_TABLE_CELL(elementUUID, parentBlockUUID);
      methods.set({
        [elementUUID]: defaultCellContent
      });
      // TODO : Auto select the text element to avoid third click
      return () => {
        const existingElement = methods.get()[elementUUID];
        if (existingElement.contentType === TEXT_ELEMENT && existingElement.contents[0]?.content === '') {
          methods.set({
            [elementUUID]: undefined
          }); // TODO : Check if auto save has to be triggered? Mostly not
        }
      }
    }
  }, [selected]);

  if (editable) {
    if (isOver) {
      console.log("directions :: ", isOverLeft, isOverRight);
    }
    drop(cellRef);
  }

  const onHeightChange = useCallback(({ width, extraChanges = undefined, height }) => {
    const changes = { width, extraChanges, flowUUID: parentUUID };
    if (height) {
      changes.height = height;
    }
    const resizeSuccess = methods.updateGridElementStyles(elementUUID, changes);
    return resizeSuccess;
  }, []);

  const updateElementRect = useCallback(() => {
    if (moveableRef?.current?.updateRect) {
      moveableRef.current.updateRect();
    }
  }, []);

  return (
    <Content
      className={`cell ${editable && (isOver) ? 'isOver' : ''} ${element !== undefined && 'not-empty'}`}
      ref={cellRef}
      id={elementUUID}
      verticalAlignment={verticalAlignment}
      horizontalAlignment={horizontalAlignment}
      cellPadding={isOver && cellPadding}
    >
      {element ? (
        <PageElementsProvider isEmail={true} isComplexDesign={true} parentElements={[element]} setParentElements={updateCellContents}>
           <GridContentResizer
             height={element?.style?.height}
             width={element?.style?.width}
             onResizeStop={onHeightChange}
             handle={HANDLERS_FOR_ELEMENTS[element.uiType || element.fillableType || element.contentType]}
             className={`element-resize`}
             resizeCallback={()=>console.log('resize called....')}
             cellRef={cellRef}
             enableResize={selected}
             ref={moveableRef}
          />
            <BlockElementRenderer
              element={element}
              editable={selected}
              selected={selected}
              scaling={scaling}
              pageId={pageId}
              adjustScroll={adjustScroll}
              staticTableBlock={true}
              parentStoredElements={storedElements}
              updateParentComplexElementDetails={updateComplexElementDetails}
              parentUUID={parentUUID}
              onCellDoubleClick={onCellDoubleClick}
              updateElementRect={updateElementRect}
            />
        </PageElementsProvider>
      ) : (
        <div />
      )}
      {editable && isOver && element !== undefined && (
        <>
            <div ref={leftDrop} className={`leftDrop ${isOverLeft ? 'visible' : ''}`} />
            <div ref={rightDrop} className={`rightDrop ${isOverRight ? 'visible' : ''}`} />
            <div ref={topDrop} className={`topDrop ${isOverTop ? 'visible' : ''}`} />
            <div ref={bottomDrop} className={`bottomDrop ${isOverBottom ? 'visible' : ''}`} />
        </>
      )}
    </Content>
  );
}
const forwareRefCellContainer = React.forwardRef(CellContainer);
export default React.memo(forwareRefCellContainer);